declare var __CLIENT_ENV__: Record<string, string> | undefined;
declare var process: any;

export type EnvironmentType = {
	localApiRoot?: string | null;
	localApiTimeout?: number | null;
	apiRoot?: string | null;
	uploadRoot?: string | null;
	chatApiRoot?: string | null;
	logMode?: boolean;
	googleRedirectUrl?: string | null;
	googleAuthUrl?: string | null;
	googleClientId?: string | null;
	facebookRedirectUrl?: string | null;
	facebookAuthUrl?: string | null;
	facebookClientId?: string | null;
	firebaseConfig?: any | null;
	facebookChatId?: string | null;
	workerRoot?: string | null;
	gaKey?: string | null;
	paymentDate?: number | null;
	redirecturl?: string | null;
	slidePartner?: string | null;
	apiMap?: string | null;
	mapId?: string | null;
	apiPaypal?: string | null;
	/** When true, load PayPal JS SDK v6 from sandbox.paypal.com (use with sandbox client IDs). */
	paypalSdkSandbox?: boolean;
	apiStripe?: string | null;
	isStripe?: boolean;
	isSamsung?: boolean;
	publicSiteOrigin?: string | null;
};

const environments: EnvironmentType = {};
export default environments;

/** Keys to forward from server env to client-side injection */
export const CLIENT_ENV_KEYS = [
	"PORT",
	"LOCAL_API_ROOT",
	"LOCAL_API_TIMEOUT",
	"API_ROOT",
	"CHAT_API_ROOT",
	"LOG_MODE",
	"GOOGLE_REDIRECT_URL",
	"GOOGLE_AUTH_URL",
	"GOOGLE_CLIENT_ID",
	"FACEBOOK_REDIRECT_URL",
	"FACEBOOK_AUTH_URL",
	"FACEBOOK_CLIENT_ID",
	"FIREBASE_CONFIG",
	"FACEBOOK_CHAT_ID",
	"WORKER_ROOT",
	"UPLOAD_ROOT",
	"GA_KEY",
	"PAYMENT_DATE",
	"REDIRECT_URL",
	"SLIDE_PARTNER",
	"API_MAP",
	"MAP_ID",
	"API_PAYPAL",
	"API_PAYPAL_SANDBOX",
	"API_STRIPE",
	"IS_STRIPE",
	"IS_SAMSUNG",
	"PUBLIC_SITE_ORIGIN",
] as const;

/** Build a client-safe env object from process.env (used by SSR to inject into HTML) */
export function getClientEnv(): Record<string, string> {
	const env: Record<string, string> = {};
	CLIENT_ENV_KEYS.forEach((key) => {
		const val = typeof process !== "undefined" && process?.env?.[key];
		if (val) env[key] = val;
	});
	return env;
}

function isBrowserLocalHost() {
	if (typeof window === "undefined") return false;
	return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

export function loadEnvironments() {
	// Priority:
	// 1. window.__ENV__  — SSR runtime injection (browser in production)
	// 2. __CLIENT_ENV__ — rspack DefinePlugin build-time injection (browser in dev)
	// 3. process.env    — Node.js server-side (SSR server)
	const state: Record<string, any> =
		(typeof window !== "undefined" && (window as any).__ENV__) ||
		(typeof __CLIENT_ENV__ !== "undefined" && __CLIENT_ENV__) ||
		(typeof process !== "undefined" && process?.env) ||
		{};

	environments.localApiRoot = state.LOCAL_API_ROOT;
	environments.localApiTimeout = state.LOCAL_API_TIMEOUT ? parseInt(state.LOCAL_API_TIMEOUT) : 0;
	environments.apiRoot = state.API_ROOT;
	environments.chatApiRoot = state.CHAT_API_ROOT;
	environments.logMode = state.LOG_MODE == "true" ? true : false;
	environments.googleRedirectUrl = state.GOOGLE_REDIRECT_URL;
	environments.googleAuthUrl = state.GOOGLE_AUTH_URL;
	environments.googleClientId = state.GOOGLE_CLIENT_ID;
	environments.facebookRedirectUrl = state.FACEBOOK_REDIRECT_URL;
	environments.facebookAuthUrl = state.FACEBOOK_AUTH_URL;
	environments.facebookClientId = state.FACEBOOK_CLIENT_ID;
	environments.firebaseConfig = state.FIREBASE_CONFIG && JSON.parse(state.FIREBASE_CONFIG);
	environments.facebookChatId = state.FACEBOOK_CHAT_ID;
	environments.workerRoot = state.WORKER_ROOT;
	environments.uploadRoot = state.UPLOAD_ROOT;
	environments.gaKey = state.GA_KEY;
	environments.paymentDate = state.PAYMENT_DATE ? parseInt(state.PAYMENT_DATE) : 25;
	environments.redirecturl = state.REDIRECT_URL;
	environments.slidePartner = state.SLIDE_PARTNER;
	environments.apiMap = state.API_MAP;
	environments.mapId = state.MAP_ID;
	environments.apiPaypal = state.API_PAYPAL;
	environments.paypalSdkSandbox = String(state.API_PAYPAL_SANDBOX || "").toLowerCase() === "true";
	environments.apiStripe = state.API_STRIPE;
	environments.isStripe = state.IS_STRIPE == "true" ? true : false;
	environments.isSamsung = state.IS_SAMSUNG == "true" ? true : false;
	environments.publicSiteOrigin = state.PUBLIC_SITE_ORIGIN
		? String(state.PUBLIC_SITE_ORIGIN).replace(/\/$/, "")
		: null;

	// Fallback for local development when .env is not provided.
	// Backend default port is 4000 (3000/3100/3510 hit Windows Hyper-V excluded ranges).
	if (isBrowserLocalHost()) {
		environments.localApiRoot = environments.localApiRoot || "http://localhost:4000/api";
		environments.apiRoot = environments.apiRoot || environments.localApiRoot;
	}
}

loadEnvironments();
